import { Method } from 'axios';

import { HttpResponseCodes } from '../http/axios/response';
import I18n from '../i18n';
import { logout } from '@wetransfer/transfer/auth-bundle/helpers/auth0';
import { checkIfSSOIsEnabled } from '@wetransfer/transfer/auth-bundle/helpers/sso';
import getEnv from '@wetransfer/transfer/config/env';
import { removeCurrentTeamCookie } from '@wetransfer/transfer/hooks/useCurrentTeam/useCurrentTeam';
import request from '@wetransfer/transfer/modules/http/axios/request';
import { getDomainWithoutSubdomain } from '@wetransfer/transfer/modules/url';

interface Config {
  path: string;
  method?: Method;
  pathExt?: boolean;
}

interface Options {
  pathExtValues?: Record<string, string>;
  headers?: Record<string, unknown>;
}

// Only throw log out warning once
let forcedLoggedOut = false;

// This method generates requests based on the setup
function generateRequest(config: Config) {
  return (
    params: { name: string; value: string } = null,
    options: Options = {}
  ) => {
    if (!config) {
      throw new Error('InvalidApiCall');
    }

    // Figure out parameters
    let path = `${getEnv().NEXT_PUBLIC_API_URL}${config.path}`;

    // Do we like to extend/replace the path with a variable?
    if (config.pathExt && options.pathExtValues) {
      Object.keys(options.pathExtValues).forEach((pathExtName) => {
        path = path.replace(
          `{${pathExtName}}`,
          options.pathExtValues[pathExtName]
        );
      });
    }

    return request({
      method: config.method,
      path,
      params,
      headers: options.headers,
    }).catch((error) => {
      if (
        !forcedLoggedOut &&
        error.status === HttpResponseCodes.UNAUTHORIZED &&
        error.body.message === 'Unauthorized'
      ) {
        forcedLoggedOut = true;

        // eslint-disable-next-line no-alert
        alert(I18n.t('notification.logged_out'));

        if (checkIfSSOIsEnabled()) {
          void logout();

          return;
        }

        location.reload();

        return;
      }

      if (
        (error.status === HttpResponseCodes.FORBIDDEN ||
          error.status === HttpResponseCodes.NOT_FOUND) &&
        error.body.message === 'Team not found'
      ) {
        const cookieDomain = `.${getDomainWithoutSubdomain(
          window.location.href
        )}`;
        removeCurrentTeamCookie(cookieDomain);
        location.reload();

        return;
      }

      // Throw the error again to continue execution, as this error will be caught
      // in other parts of the code.
      throw error;
    });
  };
}

export default function Apify<Methods>(
  setup: Record<keyof Methods, Config>
): Record<keyof Methods, (...args: unknown[]) => Promise<unknown>> {
  // Create an object for all these API calls
  const transformed = {} as Record<
    keyof Methods,
    ReturnType<typeof generateRequest>
  >;

  // Loop through all the calls.
  for (const key in setup) {
    transformed[key] = generateRequest(setup[key]);
  }

  return transformed;
}
